import { useRef, useState } from 'react';
import { useUiStore, type TuningSet } from '@store/index';

const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

interface Props {
  set: TuningSet;
  onSetBass: (midi: number, cents: number) => void;
  onSetLowest: (degreeIdx: number) => void;
  onSetLowestOctave: (octave: number) => void;
  /** Retune a degree to an arbitrary position inside the scale — the
   *  caller decomposes totalCents into (semitones, cents) as needed. */
  onSetDegreeInterval: (degreeIdx: number, totalCents: number) => void;
}

// Single SVG canvas holding the whole tuning control — R slider at the
// top, scale circle in the middle, lowest-string register control at
// the bottom — so it reads as one integrated system instead of three.
const VB_W = 280;
const PAD_TOP = 60;
const CIRCLE_BOX = 240;
const PAD_BOT = 60;
const VB_H = PAD_TOP + CIRCLE_BOX + PAD_BOT; // 360
const CX = VB_W / 2; // 140
const CY = PAD_TOP + CIRCLE_BOX / 2; // 180
const R = 92;

// R slider (top): horizontal rail from x=SLIDER_X0 to x=SLIDER_X1,
// rail y = SLIDER_Y. 12 chromatic positions.
const SLIDER_X0 = 20;
const SLIDER_X1 = VB_W - 20;
const SLIDER_Y = 30;
const SLIDER_STEPS = 12;

// Lowest register control (bottom).
const REG_Y = PAD_TOP + CIRCLE_BOX + 28;

/**
 * One unified SVG: R slider → scale circle → lowest-register control.
 * Tether lines make the topology obvious: the slider above feeds the
 * R-at-12-o'clock dot; the lowest register below targets whichever
 * dot is currently marked as the lowest string.
 *
 * Interactions:
 *   Tap/drag on the R rail     → picks the chromatic pitch (µ mode
 *                                 allows sub-semitone placement).
 *   Tap a degree dot           → that degree becomes the lowest
 *                                 string (check-mark semantics).
 *   Drag a non-root dot        → micro-retune that degree ±50¢.
 *   Tap "− / +" at the bottom  → shift the lowest string's register.
 *
 * R is the scale's origin (always 0¢ from itself) and is pinned to 12
 * o'clock. Dragging the R dot is intentionally a no-op.
 */
export function ScaleCircle({
  set,
  onSetBass,
  onSetLowest,
  onSetLowestOctave,
  onSetDegreeInterval,
}: Props) {
  const flashIdx = useUiStore((s) => s.flashStringIdx);
  const svgRef = useRef<SVGSVGElement>(null);
  const [draggingDegree, setDraggingDegree] = useState<number | null>(null);
  const [snap, setSnap] = useState<'semi' | 'micro'>('semi');
  const movedRef = useRef(false);
  const sliderActive = useRef(false);

  const hue = (i: number) => (i * 360) / Math.max(1, set.scaleSize);
  const rootIdx = NOTE_NAMES.indexOf(set.bassPitchClass.toUpperCase());
  const nameAtSemi = (semi: number) =>
    NOTE_NAMES[((rootIdx + semi) % 12 + 12) % 12]!;

  const toXY = (angleDeg: number, radius = R) => {
    const a = ((angleDeg - 90) * Math.PI) / 180;
    return { x: CX + radius * Math.cos(a), y: CY + radius * Math.sin(a) };
  };

  // Convert a client event to SVG viewBox coordinates.
  const toSvgXY = (clientX: number, clientY: number) => {
    const el = svgRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    const sx = ((clientX - rect.left) / rect.width) * VB_W;
    const sy = ((clientY - rect.top) / rect.height) * VB_H;
    return { sx, sy };
  };

  const centsAtPointer = (clientX: number, clientY: number): number | null => {
    const p = toSvgXY(clientX, clientY);
    if (!p) return null;
    let rad = Math.atan2(p.sx - CX, -(p.sy - CY));
    if (rad < 0) rad += 2 * Math.PI;
    return (rad / (2 * Math.PI)) * set.scalePeriod;
  };

  // ---- Degree-dot handlers ----
  const onDotDown = (i: number) => (e: React.PointerEvent) => {
    e.stopPropagation();
    setDraggingDegree(i);
    movedRef.current = false;
    (e.target as Element).setPointerCapture?.(e.pointerId);
  };
  const onDotMove = (i: number) => (e: React.PointerEvent) => {
    if (draggingDegree !== i) return;
    if (i === 0) return; // root is locked at 12 o'clock
    const c = centsAtPointer(e.clientX, e.clientY);
    if (c === null) return;
    const size = set.scaleSize;
    const period = set.scalePeriod;
    // Bound this dot to the corridor between its immediate neighbours,
    // wrapping across the root/period seam. Margin keeps dots from
    // overlapping or swapping order.
    const MARGIN = 8;
    const prev = set.scaleNotes[(i - 1 + size) % size]!;
    const next = set.scaleNotes[(i + 1) % size]!;
    const prevCents = prev.semitones * 100 + prev.cents;
    const nextCentsRaw = next.semitones * 100 + next.cents;
    const nextCents = (i + 1) % size === 0 ? period : nextCentsRaw;
    const cur = set.scaleNotes[i]!;
    const curCents = cur.semitones * 100 + cur.cents;
    // Move the pointer's cents to the rep closest to this dot so wrap
    // around the seam doesn't snap it to the wrong corridor.
    let cAligned = c;
    if (cAligned - curCents > period / 2) cAligned -= period;
    if (cAligned - curCents < -period / 2) cAligned += period;
    const clamped = Math.max(
      prevCents + MARGIN,
      Math.min(nextCents - MARGIN, cAligned),
    );
    if (Math.abs(cAligned - curCents) > 3) movedRef.current = true;
    onSetDegreeInterval(i, clamped);
  };
  const onDotUp = (i: number) => (e: React.PointerEvent) => {
    e.stopPropagation();
    if (draggingDegree === i && !movedRef.current) onSetLowest(i);
    setDraggingDegree(null);
    movedRef.current = false;
  };

  // ---- R slider handlers ----
  const pickBassFromPointer = (clientX: number) => {
    const p = toSvgXY(clientX, 0);
    if (!p) return;
    const x = Math.max(SLIDER_X0, Math.min(SLIDER_X1, p.sx));
    const posFloat = ((x - SLIDER_X0) / (SLIDER_X1 - SLIDER_X0)) * SLIDER_STEPS;
    const curBassMidi =
      (set.bassOctave + 1) * 12 +
      NOTE_NAMES.indexOf(set.bassPitchClass.toUpperCase());
    const curOct = Math.floor(curBassMidi / 12);
    if (snap === 'semi') {
      const pcIdx = Math.max(0, Math.min(11, Math.round(posFloat)));
      onSetBass(curOct * 12 + pcIdx, 0);
    } else {
      const pcIdx = Math.max(0, Math.min(11, Math.floor(posFloat)));
      const frac = posFloat - pcIdx;
      const cents = (frac - 0.5) * 100;
      onSetBass(
        curOct * 12 + pcIdx,
        Math.max(-50, Math.min(50, cents + 50)),
      );
    }
  };
  const onSliderDown = (e: React.PointerEvent) => {
    sliderActive.current = true;
    (e.target as Element).setPointerCapture?.(e.pointerId);
    pickBassFromPointer(e.clientX);
  };
  const onSliderMove = (e: React.PointerEvent) => {
    if (!sliderActive.current) return;
    if ((e.buttons & 1) === 0) {
      sliderActive.current = false;
      return;
    }
    pickBassFromPointer(e.clientX);
  };
  const onSliderUp = () => {
    sliderActive.current = false;
  };

  // R thumb x-position on the rail.
  const bassPcIdx = NOTE_NAMES.indexOf(set.bassPitchClass.toUpperCase());
  const thumbFrac = (bassPcIdx + set.bassCents / 100 + 0.0) / SLIDER_STEPS;
  const thumbX = SLIDER_X0 + thumbFrac * (SLIDER_X1 - SLIDER_X0);

  // Chromatic ticks around the ring (every 100¢).
  const chromaticTicks: { a: number; semi: number }[] = [];
  const tickCount = Math.floor(set.scalePeriod / 100);
  for (let i = 0; i < tickCount; i++) {
    chromaticTicks.push({ a: (i * 100 / set.scalePeriod) * 360, semi: i });
  }

  // Interval-cents labels: between consecutive dots (wraps at end).
  const intervalLabels = set.scaleNotes.map((n, i) => {
    const nextI = (i + 1) % set.scaleSize;
    const next = set.scaleNotes[nextI]!;
    const thisCents = n.semitones * 100 + n.cents;
    let nextCents = next.semitones * 100 + next.cents;
    if (nextI === 0) nextCents = set.scalePeriod;
    const delta = Math.round(nextCents - thisCents);
    const midAngle = ((thisCents + nextCents) / 2 / set.scalePeriod) * 360;
    const p = toXY(midAngle, R - 22);
    return { x: p.x, y: p.y, delta };
  });

  // Lowest degree dot position (for tether line down to register).
  const lowestIdx = set.lowestDegreeIdx ?? 0;
  const lowestNote = set.scaleNotes[lowestIdx] ?? { semitones: 0, cents: 0 };
  const lowestCents = lowestNote.semitones * 100 + lowestNote.cents;
  const lowestAngle = (lowestCents / set.scalePeriod) * 360;
  const lowestPos = toXY(lowestAngle);
  // The register display should reflect the LOWEST STRING's octave, not
  // the root's. When (rootIdx + lowestNote.semitones) crosses 12 the
  // bottom string spills into the next scientific octave — e.g. root
  // A2 + 7 semitones (the E-degree of the In scale) = E3, not E2.
  const lowestOctaveOffset = Math.floor(
    (rootIdx + lowestNote.semitones) / 12,
  );
  const displayedRegister = set.bassOctave + lowestOctaveOffset;

  // Perfect-5th degree — nearest to 700¢ (within 100¢). Shares the
  // red HOME colour with the root so both stable tones stand out.
  // Identical rule to DerivedString.isPerfectFifth in tuningStore.
  let p5Idx = -1;
  {
    let bestDelta = Infinity;
    for (let d = 1; d < set.scaleNotes.length; d++) {
      const nn = set.scaleNotes[d]!;
      const total = nn.semitones * 100 + nn.cents;
      const delta = Math.abs(total - 700);
      if (delta < bestDelta) {
        bestDelta = delta;
        p5Idx = d;
      }
    }
    if (bestDelta > 100) p5Idx = -1;
  }

  // Register tap zones (at bottom).
  const regMinusX = CX - 46;
  const regPlusX = CX + 46;
  const regValueX = CX;

  return (
    <div className="scale-circle">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        className="scale-circle__svg"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* ---------- R slider rail (top) ---------- */}
        <text
          x={SLIDER_X0}
          y={14}
          fontSize={11}
          fontWeight={700}
          fill="var(--key-root)"
        >
          R
        </text>
        <text
          x={SLIDER_X0 + 12}
          y={14}
          fontSize={11}
          fill="rgba(255,255,255,0.55)"
        >
          {`· ${set.bassPitchClass}${
            Math.abs(set.bassCents) >= 1
              ? ` ${set.bassCents > 0 ? '+' : ''}${Math.round(set.bassCents)}¢`
              : ''
          }`}
        </text>
        {/* Rail hit area (wider than visual rail for easy grabbing). */}
        <rect
          x={SLIDER_X0 - 6}
          y={SLIDER_Y - 12}
          width={SLIDER_X1 - SLIDER_X0 + 12}
          height={24}
          fill="transparent"
          onPointerDown={onSliderDown}
          onPointerMove={onSliderMove}
          onPointerUp={onSliderUp}
          onPointerCancel={onSliderUp}
          style={{ cursor: 'pointer', touchAction: 'none' }}
        />
        {/* Rail line */}
        <line
          x1={SLIDER_X0}
          y1={SLIDER_Y}
          x2={SLIDER_X1}
          y2={SLIDER_Y}
          stroke="rgba(255,255,255,0.18)"
          strokeWidth={2}
        />
        {/* Chromatic tick labels. */}
        {Array.from({ length: SLIDER_STEPS }, (_, i) => {
          const left = SLIDER_X0 + ((i + 0.5) / SLIDER_STEPS) * (SLIDER_X1 - SLIDER_X0);
          const isCurrent = bassPcIdx === i;
          return (
            <g key={i}>
              <line
                x1={left}
                y1={SLIDER_Y - 4}
                x2={left}
                y2={SLIDER_Y + 4}
                stroke="rgba(255,255,255,0.25)"
                strokeWidth={1}
              />
              <text
                x={left}
                y={SLIDER_Y + 16}
                textAnchor="middle"
                fontSize={9}
                fill={isCurrent ? 'var(--key-root)' : 'rgba(255,255,255,0.55)'}
                fontWeight={isCurrent ? 700 : 500}
                style={{ pointerEvents: 'none' }}
              >
                {NOTE_NAMES[i]}
              </text>
            </g>
          );
        })}
        {/* Thumb */}
        <rect
          x={thumbX - 5}
          y={SLIDER_Y - 8}
          width={10}
          height={16}
          rx={2}
          fill="var(--key-root)"
          stroke="rgba(0,0,0,0.5)"
          strokeWidth={1}
          style={{ pointerEvents: 'none' }}
        />
        {/* Tether: dashed line from the thumb down to the top of the ring. */}
        <line
          x1={thumbX}
          y1={SLIDER_Y + 10}
          x2={CX}
          y2={CY - R - 6}
          stroke="var(--key-root)"
          strokeWidth={1}
          strokeDasharray="2 3"
          opacity={0.55}
        />

        {/* ---------- Scale ring ---------- */}
        <circle
          cx={CX}
          cy={CY}
          r={R}
          fill="none"
          stroke="rgba(255,255,255,0.12)"
          strokeWidth={2}
        />
        {chromaticTicks.map((t) => {
          const isOct = t.semi % 12 === 0;
          const pIn = toXY(t.a, R - (isOct ? 6 : 4));
          const pOut = toXY(t.a, R + (isOct ? 4 : 2));
          return (
            <line
              key={t.semi}
              x1={pIn.x}
              y1={pIn.y}
              x2={pOut.x}
              y2={pOut.y}
              stroke={isOct ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.1)'}
              strokeWidth={isOct ? 1.25 : 1}
            />
          );
        })}
        {/* Interval-cents labels between consecutive dots — hidden for
            busy scales (>8 notes) where they start overlapping dots. */}
        {set.scaleSize <= 8 &&
          intervalLabels.map((lbl, i) => (
            <text
              key={`iv-${i}`}
              x={lbl.x}
              y={lbl.y}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={7.5}
              fill="rgba(255,255,255,0.3)"
              style={{ pointerEvents: 'none' }}
            >
              {lbl.delta}¢
            </text>
          ))}
        {/* Degree dots. */}
        {set.scaleNotes.map((n, i) => {
          const cAbs = n.semitones * 100 + n.cents;
          const angle = (cAbs / set.scalePeriod) * 360;
          const { x, y } = toXY(angle);
          const isRoot = i === 0;
          const isP5 = i === p5Idx;
          const isHome = isRoot || isP5;
          const isLowest = i === lowestIdx;
          const radius = isRoot ? 17 : isP5 ? 15 : 13;
          const fill = isRoot
            ? 'var(--key-root)'
            : isP5
              ? 'var(--key-fifth)'
              : `hsl(${hue(i)}, 68%, 60%)`;
          const labelPos = toXY(angle, R + 18);
          const pitchName = nameAtSemi(n.semitones);
          const isFlashing =
            flashIdx !== null &&
            ((flashIdx + lowestIdx) % set.scaleSize === i);
          return (
            <g key={i}>
              <g
                onPointerDown={onDotDown(i)}
                onPointerMove={onDotMove(i)}
                onPointerUp={onDotUp(i)}
                onPointerCancel={() => {
                  setDraggingDegree(null);
                  movedRef.current = false;
                }}
                style={{ cursor: 'pointer', touchAction: 'none' }}
              >
                <circle
                  cx={x}
                  cy={y}
                  r={isFlashing ? radius + 4 : radius}
                  fill={fill}
                  stroke={
                    isFlashing
                      ? '#fff'
                      : isLowest
                        ? '#fff'
                        : 'rgba(0,0,0,0.45)'
                  }
                  strokeWidth={isFlashing ? 3 : isLowest ? 2 : 2}
                  strokeDasharray={isLowest && !isFlashing ? '3 3' : undefined}
                  style={{ transition: 'r 80ms, stroke 80ms' }}
                />
                <text
                  x={x}
                  y={y}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize={10}
                  fontWeight={700}
                  fill="rgba(0,0,0,0.8)"
                  style={{ pointerEvents: 'none' }}
                >
                  {i + 1}
                </text>
              </g>
              <text
                x={labelPos.x}
                y={labelPos.y}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={isRoot ? 11 : isP5 ? 10 : 9.5}
                fontWeight={isHome ? 700 : 500}
                fill={
                  isRoot
                    ? 'var(--key-root)'
                    : isP5
                      ? 'var(--key-fifth)'
                      : 'rgba(255,255,255,0.7)'
                }
                style={{ pointerEvents: 'none' }}
              >
                {pitchName}
              </text>
              {isLowest && (
                <text
                  x={labelPos.x}
                  y={labelPos.y + 11}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize={8}
                  fill="#fff"
                  style={{ pointerEvents: 'none' }}
                >
                  ▼ lowest
                </text>
              )}
            </g>
          );
        })}

        {/* ---------- Lowest register (bottom), tethered to the lowest dot. */}
        <line
          x1={lowestPos.x}
          y1={lowestPos.y + 14}
          x2={CX}
          y2={REG_Y - 14}
          stroke="#fff"
          strokeWidth={1}
          strokeDasharray="3 3"
          opacity={0.5}
        />
        <text
          x={CX}
          y={REG_Y - 14}
          textAnchor="middle"
          fontSize={10}
          fill="rgba(255,255,255,0.55)"
          style={{ pointerEvents: 'none' }}
        >
          Lowest · degree {lowestIdx + 1} · register
        </text>
        {/* − */}
        <g
          onClick={() =>
            set.bassOctave > 1 && onSetLowestOctave(set.bassOctave - 1)
          }
          style={{ cursor: set.bassOctave > 1 ? 'pointer' : 'not-allowed' }}
        >
          <circle
            cx={regMinusX}
            cy={REG_Y + 4}
            r={12}
            fill="rgba(255,255,255,0.06)"
            stroke="rgba(255,255,255,0.2)"
            strokeWidth={1}
            opacity={set.bassOctave > 1 ? 1 : 0.35}
          />
          <text
            x={regMinusX}
            y={REG_Y + 4}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={16}
            fontWeight={700}
            fill="#fff"
            style={{ pointerEvents: 'none' }}
          >
            −
          </text>
        </g>
        {/* value */}
        <text
          x={regValueX}
          y={REG_Y + 4}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={22}
          fontWeight={700}
          fill="#fff"
          style={{ pointerEvents: 'none' }}
        >
          {displayedRegister}
        </text>
        {/* + */}
        <g
          onClick={() =>
            set.bassOctave < 8 && onSetLowestOctave(set.bassOctave + 1)
          }
          style={{ cursor: set.bassOctave < 8 ? 'pointer' : 'not-allowed' }}
        >
          <circle
            cx={regPlusX}
            cy={REG_Y + 4}
            r={12}
            fill="rgba(255,255,255,0.06)"
            stroke="rgba(255,255,255,0.2)"
            strokeWidth={1}
            opacity={set.bassOctave < 8 ? 1 : 0.35}
          />
          <text
            x={regPlusX}
            y={REG_Y + 4}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={16}
            fontWeight={700}
            fill="#fff"
            style={{ pointerEvents: 'none' }}
          >
            +
          </text>
        </g>
      </svg>

      {/* Snap-mode toggle: overlaid HTML so the 100¢/µ chips look crisp. */}
      <div className="scale-circle__modeOverlay">
        <button
          type="button"
          className={
            'scale-circle__modebtn' +
            (snap === 'semi' ? ' scale-circle__modebtn--on' : '')
          }
          onClick={() => setSnap('semi')}
        >
          100¢
        </button>
        <button
          type="button"
          className={
            'scale-circle__modebtn' +
            (snap === 'micro' ? ' scale-circle__modebtn--on' : '')
          }
          onClick={() => setSnap('micro')}
        >
          µ
        </button>
      </div>

      <div className="scale-circle__hint">
        tap dot → chọn làm dây thấp nhất · drag dot → micro-retune ±50¢ · thước trên → chọn R
      </div>
    </div>
  );
}
